import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { format, isToday, isYesterday } from "date-fns";
import {
  ArrowLeft,
  Trash2,
  User,
  Trophy,
  CreditCard,
  Wallet,
  UserCheck,
  Coins,
  CheckCircle2,
  Circle,
  Bell,
  ChevronRight,
  FileText,
  AlertTriangle,
  Copy,
  X,
} from "lucide-react";
import {
  AdminPopup,
  ConfirmDialog,
  Empty,
  Pill,
  Skeleton,
} from "../adminLayout"; // adjust the path if needed
import "./notification.css";

/* Types shown in the "Payments" category
   INVOICE_RECEIPT_SUBMITTED = invoice receipt sent by a client */
const PAYMENT_TYPES = [
  "PAYMENT_MADE",
  "PAYMENT_ACCEPTED",
  "PAYMENT_REJECTED",
  "INVOICE_RECEIPT_SUBMITTED",
];
const WITHDRAWAL_TYPES = [
  "WITHDRAWAL_MADE",
  "WITHDRAW_ACCEPTED",
  "WITHDRAW_REJECTED",
];
const PROFILE_TYPES = ["PROFIL_CREATED", "SUBSCRIPTION_RENEWAL_REQUESTED"];
/* Staking requests sent by VIP clients */
const STAKING_TYPES = ["STAKE_REQUESTED"];

/* Small label shown on each notification */
const TYPE_LABELS = {
  USER_CREATED: "New user",
  QUIZ_PASSED: "Quiz passed",
  PAYMENT_MADE: "Payment",
  PAYMENT_ACCEPTED: "Accepted",
  PAYMENT_REJECTED: "Rejected",
  INVOICE_RECEIPT_SUBMITTED: "Receipt",
  WITHDRAWAL_MADE: "Request",
  WITHDRAW_ACCEPTED: "Accepted",
  WITHDRAW_REJECTED: "Rejected",
  PROFIL_CREATED: "New profile",
  SUBSCRIPTION_RENEWAL_REQUESTED: "Renewal",
  STAKE_REQUESTED: "Staking",
};

/* Configuration of the 6 categories (cards on the home screen) */
const CATEGORIES = [
  {
    key: "USER_CREATED",
    label: "User notifications",
    title: "Users",
    noun: "user",
    Icon: User,
    tone: "green",
  },
  {
    key: "QUIZ",
    label: "Quiz scores",
    title: "Quiz score",
    noun: "quiz",
    Icon: Trophy,
    tone: "blue",
  },
  {
    key: "PAYMENT",
    label: "Payment notifications",
    title: "Payments",
    noun: "payment",
    Icon: CreditCard,
    tone: "amber",
  },
  {
    key: "WITHDRAWAL",
    label: "Withdrawal notifications",
    title: "Withdrawals",
    noun: "withdrawal",
    Icon: Wallet,
    tone: "purple",
  },
  {
    key: "PROFILE",
    label: "Profile notifications",
    title: "Profiles",
    noun: "profile",
    Icon: UserCheck,
    tone: "pink",
  },
  {
    key: "STAKING",
    label: "Staking notifications",
    title: "Staking",
    noun: "staking",
    Icon: Coins,
    tone: "teal",
  },
];

const getCategory = (key) => CATEGORIES.find((c) => c.key === key);

/* Does a notification belong to the selected category? */
const matchesType = (n, selectedType) => {
  if (selectedType === "USER_CREATED") return n.type === "USER_CREATED";
  if (selectedType === "QUIZ") return n.type === "QUIZ_PASSED";
  if (selectedType === "PAYMENT") return PAYMENT_TYPES.includes(n.type);
  if (selectedType === "WITHDRAWAL") return WITHDRAWAL_TYPES.includes(n.type);
  if (selectedType === "PROFILE") return PROFILE_TYPES.includes(n.type);
  if (selectedType === "STAKING") return STAKING_TYPES.includes(n.type);
  return false;
};

/* Unread counters per category ("admin" AND "commercial_admin") */
const calculateUnreadCounts = (notifs) => {
  const counts = {
    USER_CREATED: 0,
    QUIZ: 0,
    PAYMENT: 0,
    WITHDRAWAL: 0,
    PROFILE: 0,
    STAKING: 0,
  };
  notifs.forEach((n) => {
    if (n.read || !["admin", "commercial_admin"].includes(n.recipientRole))
      return;
    if (n.type === "USER_CREATED") counts.USER_CREATED++;
    if (n.type === "QUIZ_PASSED") counts.QUIZ++;
    if (PAYMENT_TYPES.includes(n.type)) counts.PAYMENT++;
    if (WITHDRAWAL_TYPES.includes(n.type)) counts.WITHDRAWAL++;
    if (PROFILE_TYPES.includes(n.type)) counts.PROFILE++;
    if (STAKING_TYPES.includes(n.type)) counts.STAKING++;
  });
  return counts;
};

/* Status badge color */
const pillTone = (status) => {
  const s = String(status || "").toLowerCase();
  if (["approved", "paid", "active", "verified", "completed"].includes(s))
    return "ok";
  if (["rejected", "expired"].includes(s)) return "bad";
  return "warn";
};

const formatDate = (dateString) => {
  if (!dateString) return "Unknown date";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Unknown date";
  return format(date, "dd/MM/yyyy HH:mm");
};

const shortDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date.toLocaleDateString("en-GB");
};

const shortTime = (dateString) => {
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? "" : format(date, "HH:mm");
};

const dayLabel = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Earlier";
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return format(date, "dd MMM yyyy");
};

const initials = (...parts) =>
  parts
    .filter(Boolean)
    .map((p) => String(p).trim()[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";

const copyText = (text) => {
  if (!navigator.clipboard) return toast.error("Copy is not supported here");
  navigator.clipboard
    .writeText(String(text))
    .then(() => toast.success("Copied"))
    .catch(() => toast.error("Unable to copy"));
};

/* Extracts "iPhone model - color" from a pack name like "Pack N (iPhone 13 Pro - Silver)" */
const extractIphoneInfo = (text = "") => {
  const match = String(text).match(/\(([^)]+)\)/);
  if (!match) return { model: null, color: null, cleanName: text };

  const parts = match[1].split(" - ");
  return {
    model: parts[0]?.trim() || null,
    color: parts[1]?.trim() || null,
    cleanName: String(text).replace(/\s*\([^)]*\)/, "").trim(),
  };
};

/* Staking plan label */
const stakePlanLabel = (planId) =>
  planId === "12m" ? "1 year" : planId === "6m" ? "6 months" : planId;

/* Step sequence of an admin decision (payment, withdrawal, staking) */
const decisionSteps = (firstLabel, status) => {
  const s = String(status || "pending").toLowerCase();
  const decided = ["approved", "rejected", "paid"].includes(s);
  return [
    { label: firstLabel, state: "done" },
    { label: "Your review", state: decided ? "done" : "current" },
    {
      label: s === "rejected" ? "Rejected" : "Approved",
      state: s === "rejected" ? "bad" : decided ? "done" : "todo",
    },
  ];
};

/* ── Small display components ── */
const InfoRow = ({ label, value, copy }) => {
  const has = Boolean(value || value === 0);
  return (
    <div className="ntf-row">
      <span className="ntf-row-label">{label}</span>
      <span className="ntf-row-value">
        {has ? value : "N/A"}
        {copy && has && (
          <button
            type="button"
            className="ntf-copy"
            title="Copy"
            aria-label={`Copy ${label}`}
            onClick={() => copyText(value)}
          >
            <Copy size={13} />
          </button>
        )}
      </span>
    </div>
  );
};

/* Header: avatar (initials) or category icon + status */
const Hero = ({ Icon, tone, avatar, title, subtitle, status }) => (
  <div className="ntf-hero">
    {avatar ? (
      <span className="ntf-avatar" aria-hidden="true">
        {avatar}
      </span>
    ) : (
      <span className={`ntf-icon lg ${tone || ""}`} aria-hidden="true">
        {Icon && <Icon size={18} />}
      </span>
    )}
    <div className="ntf-hero-text">
      <h4>{title}</h4>
      {subtitle && <p>{subtitle}</p>}
    </div>
    {status && <Pill tone={pillTone(status)}>{status}</Pill>}
  </div>
);

const Section = ({ title, children }) => (
  <section className="ntf-section">
    <h5 className="ntf-section-title">{title}</h5>
    <div className="ntf-info">{children}</div>
  </section>
);

const Stats = ({ items }) => (
  <div className="ntf-stats">
    {items.map((s) => (
      <div key={s.label} className={`ntf-stat ${s.tone || ""}`}>
        <span className="ntf-stat-label">{s.label}</span>
        <span className="ntf-stat-value">
          {s.value || s.value === 0 ? s.value : "N/A"}
        </span>
      </div>
    ))}
  </div>
);

const ProgressBar = ({ value, max, tone }) => {
  const pct =
    max > 0 ? Math.min(100, (value / max) * 100) : value > 0 ? 100 : 0;
  return (
    <div
      className="ntf-progress"
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <span
        className={`ntf-progress-bar ${tone || ""}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

const Stepper = ({ steps }) => (
  <div className="ntf-stepper">
    {steps.map((s, i) => (
      <React.Fragment key={s.label}>
        <div className={`ntf-step ${s.state}`}>
          {s.state === "done" ? (
            <CheckCircle2 size={15} />
          ) : s.state === "bad" ? (
            <AlertTriangle size={15} />
          ) : (
            <Circle size={15} />
          )}
          <span>{s.label}</span>
        </div>
        {i < steps.length - 1 && <div className="ntf-step-line" />}
      </React.Fragment>
    ))}
  </div>
);

/* Receipt: clickable image (full-screen preview) or PDF link */
const ReceiptCard = ({ title, src, pdf, onOpen }) => (
  <div className="ntf-receipt">
    <div className="ntf-receipt-head">{title}</div>
    {pdf ? (
      <a
        className="ntf-receipt-pdf"
        href={src}
        target="_blank"
        rel="noreferrer"
      >
        <FileText size={16} /> Open PDF
      </a>
    ) : (
      <button
        type="button"
        className="ntf-zoom"
        title="View full size"
        onClick={() => onOpen({ src, title })}
      >
        <img src={src} alt={title} className="ntf-receipt-img" />
      </button>
    )}
  </div>
);

/* ID document (full-screen preview) */
const MediaImage = ({ title, src, onOpen }) => (
  <div className="ntf-media">
    <h5>{title}</h5>
    {src ? (
      <button
        type="button"
        className="ntf-zoom"
        title="View full size"
        onClick={() => onOpen({ src, title })}
      >
        <img src={src} alt={title} />
      </button>
    ) : (
      <span>Not available</span>
    )}
  </div>
);

/* Full-screen preview (rendered in <body> to appear above the popup) */
const Lightbox = ({ preview, onClose }) =>
  createPortal(
    <div
      className="ntf-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={preview.title}
      onClick={onClose}
    >
      <button
        type="button"
        className="ntf-lightbox-close"
        aria-label="Close preview"
        onClick={onClose}
      >
        <X size={20} />
      </button>
      <img
        src={preview.src}
        alt={preview.title}
        onClick={(e) => e.stopPropagation()}
      />
    </div>,
    document.body,
  );

const Notifications = ({ onClose, onUnreadChange }) => {
  const [validPoints, setValidPoints] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [viewMode, setViewMode] = useState("types");
  const [listFilter, setListFilter] = useState("all");
  const [error, setError] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [withdrawDetails, setWithdrawDetails] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [profileDetails, setProfileDetails] = useState(null);
  const [stakeDetails, setStakeDetails] = useState(null); // staking request to review
  const [rejectReason, setRejectReason] = useState(""); // optional reason when rejecting a stake
  const [invoiceDetails, setInvoiceDetails] = useState(null); // invoice whose receipt must be checked
  const [receiptView, setReceiptView] = useState(null); // { url, isPdf } of the receipt
  const [preview, setPreview] = useState(null); // { src, title } full-screen preview
  const [confirm, setConfirm] = useState(null);
  const [loading, setLoading] = useState(true); // first load only
  const [loaded, setLoaded] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const onUnreadRef = useRef(onUnreadChange);
  onUnreadRef.current = onUnreadChange;

  /* Request token: ignores responses for a notification that was already left */
  const requestRef = useRef(0);
  /* Blob URL of the current receipt (to be released) */
  const receiptUrlRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const authCfg = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

  const revokeReceiptUrl = () => {
    if (receiptUrlRef.current) {
      window.URL.revokeObjectURL(receiptUrlRef.current);
      receiptUrlRef.current = null;
    }
  };

  /* Releases the blob URL when the component closes */
  useEffect(() => revokeReceiptUrl, []);

  /* Counters derived from the list: always up to date, no re-download */
  const unreadCounts = useMemo(
    () => calculateUnreadCounts(notifications),
    [notifications],
  );
  const totalUnread = Object.values(unreadCounts).reduce(
    (sum, c) => sum + c,
    0,
  );

  /* Sends the real total to the sidebar (= sum of the popup badges) */
  useEffect(() => {
    if (loaded) onUnreadRef.current?.(totalUnread);
  }, [totalUnread, loaded]);

  /* Card summary: total + subtitle based on unread */
  const getCardSummary = (key) => {
    const items = notifications.filter((n) => matchesType(n, key));
    const unread = unreadCounts[key] || 0;
    let subtitle = "All caught up";
    if (unread > 0) subtitle = `${unread} to review`;
    else if (items.length === 0) subtitle = "No notifications";
    return { total: items.length, unread, subtitle };
  };

  /* Clears the details data (and releases the receipt blob URL) */
  const resetDetails = () => {
    requestRef.current += 1; // invalidates in-flight detail requests
    revokeReceiptUrl();
    setSelectedNotification(null);
    setUserDetails(null);
    setPaymentDetails(null);
    setWithdrawDetails(null);
    setProfileDetails(null);
    setStakeDetails(null);
    setRejectReason("");
    setInvoiceDetails(null);
    setReceiptView(null);
    setPreview(null);
    setDetailLoading(false);
  };

  const togglePopup = (type = null, mode = "types") => {
    setSelectedType(type);
    setViewMode(mode);
    setConfirm(null);
    setError(null);
    if (mode === "types" || mode === "list") {
      resetDetails();
      setListFilter("all");
    }
  };

  const closePopup = () => {
    onClose?.();
    setViewMode("types");
    setSelectedType(null);
    setConfirm(null);
    resetDetails();
  };

  /* Go back one level: details → list → categories → close */
  const handleBack = () => {
    if (viewMode === "details") {
      setError(null);
      resetDetails();
      setViewMode("list");
    } else if (viewMode === "list") {
      togglePopup(null, "types");
    } else {
      closePopup();
    }
  };

  /* Escape key: preview first, then confirmation, then go up one level */
  const handleEscape = () => {
    if (preview) return setPreview(null);
    if (confirm) return setConfirm(null);
    handleBack();
  };

  /* ── Load notifications (silent = automatic refresh) ── */
  const fetchAllData = async (silent = false) => {
    if (!localStorage.getItem("token")) {
      setError("Missing token. Please log in again.");
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/notifications`, authCfg());
      setNotifications(res.data || []);
      setLoaded(true);
    } catch (err) {
      console.error("Loading error:", err);
      if (!silent) setError("Unable to load notifications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(() => fetchAllData(true), 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL]);

  /* Mark as read: immediate update, then server call */
  const markAsRead = async (notifId) => {
    setNotifications((prev) =>
      prev.map((n) => (n._id === notifId ? { ...n, read: true } : n)),
    );
    try {
      await axios.post(
        `${API_URL}/notifications/${notifId}/read`,
        {},
        authCfg(),
      );
    } catch (err) {
      console.error("Mark as read error", err);
      fetchAllData(true);
    }
  };

  /* ── Helpers for admin actions ── */
  const notifyUser = (payload) =>
    axios.post(`${API_URL}/notificationsUser/send`, payload, authCfg());

  const fail = (err) => toast.error(err.response?.data?.message || err.message);

  /* After an action: toast, back to the list and refresh */
  const finish = async (message) => {
    toast.success(message || "Done");
    togglePopup(selectedType, "list");
    await fetchAllData(true);
  };

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  // === Admin actions ===
  const handleRejectProfile = async (profileId, userId) => {
    try {
      const response = await axios.put(
        `${API_URL}/profiles/${profileId}/reject`,
        {},
        authCfg(),
      );
      await notifyUser({
        userId,
        type: "PROFILE_REJECTED",
        message: "Your profile verification was rejected. Please try again.",
      });
      setNotifications((prev) => prev.filter((n) => n.profile !== profileId));
      await finish(response.data.message);
    } catch (err) {
      fail(err);
    }
  };

  const handleValidateProfile = async (profileId, userId) => {
    try {
      const response = await axios.put(
        `${API_URL}/profiles/${profileId}/verify`,
        {},
        authCfg(),
      );
      await notifyUser({
        userId,
        type: "PROFILE_VERIFIED",
        message: "Your profile is verified",
      });
      setNotifications((prev) => prev.filter((n) => n.profile !== profileId));
      await finish(response.data.message);
    } catch (err) {
      fail(err);
    }
  };

  const handleApprovePaymentForQuiz = async (quizResultId, userId) => {
    try {
      const response = await axios.post(
        `${API_URL}/approve-payment/${quizResultId}`,
        {},
        authCfg(),
      );
      await notifyUser({
        userId,
        type: "PAYMENT_APPROVED_FOR_USER",
        message: `You can now proceed with payment for pack ${response.data.quizResult.packId.packName}.`,
      });
      setNotifications((prev) =>
        prev.filter((n) => n.quizResult !== quizResultId),
      );
      await finish(response.data.message);
    } catch (err) {
      fail(err);
    }
  };

  const handleActivateSubscription = async (profileId, userId) => {
    try {
      const response = await axios.put(
        `${API_URL}/profiles/${profileId}/verify`,
        {},
        authCfg(),
      );
      setNotifications((prev) =>
        prev.filter(
          (n) =>
            !(n.user === userId && n.type === "SUBSCRIPTION_RENEWAL_REQUESTED"),
        ),
      );
      await finish(response.data.message);
    } catch (err) {
      fail(err);
    }
  };

  const handleRejectSubscription = async (userId) => {
    try {
      const response = await axios.put(
        `${API_URL}/profiles/${userId}/reject-subscription`,
        {},
        authCfg(),
      );
      setNotifications((prev) =>
        prev.filter(
          (n) =>
            !(n.user === userId && n.type === "SUBSCRIPTION_RENEWAL_REQUESTED"),
        ),
      );
      await finish(response.data.message);
    } catch (err) {
      fail(err);
    }
  };

  const handlePaymentDecision = async (paymentId, status) => {
    const approved = status === "approved";
    try {
      const response = await axios.put(
        `${API_URL}/payments/${paymentId}/validate`,
        { status },
        authCfg(),
      );
      await notifyUser({
        userId: paymentDetails.userId,
        type: approved ? "PAYMENT_ACCEPTED" : "PAYMENT_REJECTED",
        message: approved
          ? "Your payment is accepted"
          : "Your payment was rejected.",
      });
      setNotifications((prev) => prev.filter((n) => n.payment !== paymentId));
      await finish(response.data.message);
    } catch (err) {
      fail(err);
    }
  };

  const handleWithdrawAction = async (withdrawId, status) => {
    try {
      const response = await axios.put(
        `${API_URL}/admin/withdraw/${withdrawId}`,
        { status },
        authCfg(),
      );
      await notifyUser({
        userId: withdrawDetails.user,
        type: status === "approved" ? "WITHDRAW_ACCEPTED" : "WITHDRAW_REJECTED",
        message:
          status === "approved"
            ? "Your withdraw is accepted"
            : "Your withdrawal is rejected",
      });
      setNotifications((prev) =>
        prev.filter((n) => n._id !== withdrawDetails._id),
      );
      await finish(response.data.message);
    } catch (err) {
      fail(err);
    }
  };

  /* Accept / reject an invoice receipt
     The backend sets the invoice to "paid" (approve) and notifies the client */
  const handleInvoiceReceiptAction = async (invoiceId, action) => {
    try {
      const res = await axios.put(
        `${API_URL}/invoices/receipt/${invoiceId}/${action}`,
        {},
        authCfg(),
      );
      setNotifications((prev) =>
        prev.filter(
          (n) =>
            !(
              n.type === "INVOICE_RECEIPT_SUBMITTED" &&
              (n.invoice?._id || n.invoice) === invoiceId
            ),
        ),
      );
      await finish(res.data.message);
    } catch (err) {
      fail(err);
    }
  };

  /* Accept / reject a staking request (action = "approve" | "reject")
     The backend activates the stake (or refunds the points), updates the admin
     notification and notifies the client by itself. */
  const handleStakeAction = async (stakeId, action) => {
    try {
      const body =
        action === "reject" ? { reason: rejectReason.trim() || undefined } : {};
      const res = await axios.post(
        `${API_URL}/staking/admin/${stakeId}/${action}`,
        body,
        authCfg(),
      );
      await finish(res.data.message);
    } catch (err) {
      fail(err);
    }
  };

  const removeNotification = async (notifId) => {
    try {
      await axios.delete(`${API_URL}/notifications/${notifId}`, authCfg());
      setNotifications((prev) => prev.filter((n) => n._id !== notifId));
      toast.success("Notification deleted");
    } catch (err) {
      toast.error("Failed to delete notification.");
    }
  };

  // === Fetch details (isCurrent = false if the admin left the screen in the meantime) ===
  const fetchUserDetails = async (userId, isCurrent) => {
    try {
      const res = await axios.get(`${API_URL}/users/${userId}`, authCfg());
      if (isCurrent()) setUserDetails(res.data);
    } catch (err) {
      if (isCurrent()) setError("Failed to load user.");
    }
  };

  const fetchWithdrawDetails = async (notificationId, isCurrent) => {
    try {
      const notifRes = await axios.get(
        `${API_URL}/notifications/${notificationId}`,
        authCfg(),
      );
      const withdrawData = notifRes.data;
      if (!withdrawData.withdraw) {
        if (isCurrent()) setError("Withdraw ID missing.");
        return;
      }

      const walletRes = await axios.get(
        `${API_URL}/wallet/${withdrawData.user}`,
        authCfg(),
      );
      const now = new Date();
      const validPointsCalc = (walletRes.data.pointHistory || []).reduce(
        (sum, e) => (now >= new Date(e.becomesValidAt) ? sum + e.points : sum),
        0,
      );

      if (!isCurrent()) return;
      setWithdrawDetails(withdrawData);
      setValidPoints(validPointsCalc);
    } catch (err) {
      if (isCurrent()) setError("Failed to load withdrawal details.");
    }
  };

  const fetchPaymentDetails = async (userId, paymentId, isCurrent) => {
    try {
      const url = paymentId
        ? `${API_URL}/payment/${userId}/${paymentId}`
        : `${API_URL}/payment/${userId}`;
      const res = await axios.get(url, authCfg());
      if (isCurrent())
        setPaymentDetails(Array.isArray(res.data) ? res.data[0] : res.data);
    } catch (err) {
      if (isCurrent()) {
        setPaymentDetails(null);
        setError("Failed to load payment.");
      }
    }
  };

  /* Loads the invoice + the receipt file.
     The /invoices/receipt/:id route requires the Authorization header:
     we fetch the file as a blob, then display it through a local URL. */
  const fetchInvoiceDetails = async (invoiceId, isCurrent) => {
    try {
      const res = await axios.get(
        `${API_URL}/invoices/details/${invoiceId}`,
        authCfg(),
      );
      if (!isCurrent()) return;
      setInvoiceDetails(res.data);

      if (!res.data?.receiptPath) return; // no receipt uploaded: nothing to load

      const file = await axios.get(`${API_URL}/invoices/receipt/${invoiceId}`, {
        ...authCfg(),
        responseType: "blob",
      });
      if (!isCurrent()) return;
      revokeReceiptUrl();
      receiptUrlRef.current = window.URL.createObjectURL(file.data);
      setReceiptView({
        url: receiptUrlRef.current,
        isPdf: file.data.type === "application/pdf",
      });
    } catch (err) {
      if (isCurrent()) {
        setError(
          err.response?.status === 404
            ? "Receipt not found."
            : "Failed to load invoice receipt.",
        );
      }
    }
  };

  const fetchProfileByUserId = async (userId, isCurrent) => {
    try {
      const res = await axios.get(
        `${API_URL}/profiles/user/${userId}`,
        authCfg(),
      );
      if (isCurrent()) setProfileDetails(res.data);
    } catch (err) {
      if (isCurrent()) setError("Failed to load profile.");
    }
  };

  /* Loads the staking request (client info is populated by the backend) */
  const fetchStakeDetails = async (stakeId, isCurrent) => {
    if (!stakeId) {
      if (isCurrent()) setError("Stake ID missing.");
      return;
    }
    try {
      const res = await axios.get(
        `${API_URL}/staking/admin/${stakeId}`,
        authCfg(),
      );
      if (isCurrent()) setStakeDetails(res.data);
    } catch (err) {
      if (isCurrent()) {
        setError(
          err.response?.status === 404
            ? "Staking request not found."
            : "Failed to load staking request.",
        );
      }
    }
  };

  /* Click on a notification in the list */
  const handleNotificationClick = async (notif) => {
    resetDetails();
    const token = requestRef.current;
    const isCurrent = () => requestRef.current === token;

    setError(null);
    setSelectedNotification(notif);
    setViewMode("details");
    setDetailLoading(true);
    if (!notif.read) markAsRead(notif._id);

    try {
      if (selectedType === "USER_CREATED") {
        await fetchUserDetails(notif.user, isCurrent);
      } else if (selectedType === "PAYMENT") {
        if (notif.type === "INVOICE_RECEIPT_SUBMITTED") {
          await fetchInvoiceDetails(
            notif.invoice?._id || notif.invoice,
            isCurrent,
          );
        } else {
          await fetchPaymentDetails(notif.user, notif.payment, isCurrent);
        }
      } else if (selectedType === "WITHDRAWAL") {
        await fetchWithdrawDetails(notif._id, isCurrent);
      } else if (selectedType === "PROFILE") {
        await fetchProfileByUserId(notif.user, isCurrent);
      } else if (selectedType === "STAKING") {
        await fetchStakeDetails(notif.stake?._id || notif.stake, isCurrent);
      }
    } finally {
      if (isCurrent()) setDetailLoading(false);
    }
  };

  const askDeleteNotification = (notif) =>
    setConfirm({
      title: "Delete this notification?",
      message: "It will be permanently removed from the list.",
      confirmLabel: "Delete",
      danger: true,
      onConfirm: () => removeNotification(notif._id),
    });

  const runConfirm = async () => {
    const current = confirm;
    setConfirm(null);
    if (current?.onConfirm) await current.onConfirm();
  };

  /* ── Action buttons in the popup footer (depending on the displayed type) ── */
  const ask = (title, message, confirmLabel, danger, fn) => () =>
    setConfirm({
      title,
      message,
      confirmLabel,
      danger,
      onConfirm: () => run(fn),
    });

  const getActions = () => {
    if (viewMode !== "details" || detailLoading || !selectedNotification)
      return null;

    if (stakeDetails && selectedType === "STAKING") {
      const locked = stakeDetails.status !== "pending";
      return [
        {
          label: "Reject",
          kind: "danger",
          disabled: locked,
          onClick: ask(
            "Reject this staking request?",
            "The points will be returned to the client's wallet and the client will be notified.",
            "Reject",
            true,
            () => handleStakeAction(stakeDetails._id, "reject"),
          ),
        },
        {
          label: "Accept",
          kind: "primary",
          disabled: locked,
          onClick: ask(
            "Approve this staking request?",
            "The stake will become active and the lock period will start now. The client will be notified.",
            "Approve",
            false,
            () => handleStakeAction(stakeDetails._id, "approve"),
          ),
        },
      ];
    }

    if (invoiceDetails && selectedType === "PAYMENT") {
      const locked =
        invoiceDetails.status === "paid" || !invoiceDetails.receiptPath;
      return [
        {
          label: "Reject",
          kind: "danger",
          disabled: locked,
          onClick: ask(
            "Reject this receipt?",
            "The client will be notified.",
            "Reject",
            true,
            () => handleInvoiceReceiptAction(invoiceDetails._id, "reject"),
          ),
        },
        {
          label: "Accept",
          kind: "primary",
          disabled: locked,
          onClick: ask(
            "Approve this receipt?",
            "The invoice will be marked as paid.",
            "Approve",
            false,
            () => handleInvoiceReceiptAction(invoiceDetails._id, "approve"),
          ),
        },
      ];
    }

    if (paymentDetails?.userId) {
      const locked = ["rejected", "approved"].includes(paymentDetails.status);
      return [
        {
          label: "Reject",
          kind: "danger",
          disabled: locked,
          onClick: ask(
            "Reject this payment?",
            "The client will be notified.",
            "Reject",
            true,
            () => handlePaymentDecision(paymentDetails._id, "rejected"),
          ),
        },
        {
          label: "Accept",
          kind: "primary",
          disabled: locked,
          onClick: ask(
            "Approve this payment?",
            "The client will be notified.",
            "Approve",
            false,
            () => handlePaymentDecision(paymentDetails._id, "approved"),
          ),
        },
      ];
    }

    if (selectedType === "QUIZ") {
      return [
        { label: "Dismiss", kind: "ghost", onClick: () => handleBack() },
        {
          label: "Authorize payment",
          kind: "primary",
          disabled: !selectedNotification.quizResult,
          onClick: ask(
            "Authorize payment?",
            "The user will be allowed to proceed to payment for this pack.",
            "Authorize",
            false,
            () =>
              handleApprovePaymentForQuiz(
                selectedNotification.quizResult,
                selectedNotification.user,
              ),
          ),
        },
      ];
    }

    if (withdrawDetails) {
      const locked = withdrawDetails.status !== "pending";
      return [
        {
          label: "Reject",
          kind: "danger",
          disabled: locked,
          onClick: ask(
            "Reject this withdrawal?",
            "The client will be notified.",
            "Reject",
            true,
            () => handleWithdrawAction(withdrawDetails.withdraw, "rejected"),
          ),
        },
        {
          label: "Accept",
          kind: "primary",
          disabled: locked,
          onClick: ask(
            "Approve this withdrawal?",
            "The client will be notified.",
            "Approve",
            false,
            () => handleWithdrawAction(withdrawDetails.withdraw, "approved"),
          ),
        },
      ];
    }

    if (profileDetails) {
      if (profileDetails.isVerified) {
        const locked = profileDetails.subscriptionStatus === "active";
        return [
          {
            label: "Reject",
            kind: "danger",
            disabled: locked,
            onClick: ask(
              "Reject this renewal?",
              "The subscription will not be renewed.",
              "Reject",
              true,
              () => handleRejectSubscription(profileDetails.userId),
            ),
          },
          {
            label: "Activate subscription",
            kind: "primary",
            disabled: locked,
            onClick: ask(
              "Activate this subscription?",
              "The subscription will become active.",
              "Activate",
              false,
              () =>
                handleActivateSubscription(
                  profileDetails._id,
                  profileDetails.userId,
                ),
            ),
          },
        ];
      }
      return [
        {
          label: "Reject",
          kind: "danger",
          onClick: ask(
            "Reject this profile?",
            "The user will be asked to try again.",
            "Reject",
            true,
            () =>
              handleRejectProfile(profileDetails._id, profileDetails.userId),
          ),
        },
        {
          label: "Verify",
          kind: "primary",
          onClick: ask(
            "Verify this profile?",
            "The user will be notified.",
            "Verify",
            false,
            () =>
              handleValidateProfile(profileDetails._id, profileDetails.userId),
          ),
        },
      ];
    }

    return null;
  };

  /* ── Render the details content ── */
  const renderDetails = () => {
    if (detailLoading) return <Skeleton rows={4} />;
    const n = selectedNotification;
    const cat = getCategory(selectedType);

    /* ── User ── */
    if (userDetails && selectedType === "USER_CREATED") {
      const fullName =
        `${userDetails.name || ""} ${userDetails.surname || ""}`.trim();
      return (
        <>
          <Hero
            avatar={initials(userDetails.name, userDetails.surname)}
            title={fullName || "New user"}
            subtitle={`Registered ${formatDate(n.createdAt)}`}
          />
          <Section title="Account">
            <InfoRow label="Name" value={userDetails.name} />
            <InfoRow label="Surname" value={userDetails.surname} />
            <InfoRow label="numTel" value={userDetails.numTel} />
            <InfoRow label="Email" value={userDetails.email} copy />
          </Section>
        </>
      );
    }

    /* ── Staking request ── */
    if (stakeDetails && selectedType === "STAKING") {
      const s = stakeDetails;
      const client = s.userId && typeof s.userId === "object" ? s.userId : null;
      const clientName =
        `${client?.name || ""} ${client?.surname || ""}`.trim();
      const stepStatus = ["active", "completed"].includes(s.status)
        ? "approved"
        : s.status;
      return (
        <>
          <Hero
            Icon={cat.Icon}
            tone={cat.tone}
            title="Staking request"
            subtitle={formatDate(s.createdAt || n.createdAt)}
            status={s.status}
          />
          <Stats
            items={[
              { label: "Points to stake", value: s.amount, tone: "accent" },
              {
                label: "Reward",
                value: s.rewardPoints,
                tone: s.rewardPoints > 0 ? "ok" : "",
              },
            ]}
          />
          <Stepper steps={decisionSteps("Request sent", stepStatus)} />
          {s.status === "pending" && (
            <div className="ntf-callout">
              <AlertTriangle size={16} />
              The points are already locked in the client's wallet. Rejecting
              the request will refund them.
            </div>
          )}
          <Section title="Client">
            <InfoRow label="Name" value={clientName} />
            <InfoRow label="Email" value={client?.email} copy />
            <InfoRow
              label="User ID"
              value={client?._id || (client ? null : s.userId)}
              copy
            />
          </Section>
          <Section title="Staking">
            <InfoRow label="Duration" value={stakePlanLabel(s.planId)} />
            <InfoRow label="Level" value={s.level} />
            <InfoRow
              label="Reward rate"
              value={
                s.rewardPercent || s.rewardPercent === 0
                  ? `${s.rewardPercent}%`
                  : null
              }
            />
            <InfoRow label="Reward points" value={s.rewardPoints} />
            <InfoRow label="Start date" value={shortDate(s.startDate)} />
            <InfoRow label="End date" value={shortDate(s.endDate)} />
            {s.status === "rejected" && s.rejectReason && (
              <InfoRow label="Reject reason" value={s.rejectReason} />
            )}
          </Section>
          {s.status === "pending" && (
            <section className="ntf-section">
              <h5 className="ntf-section-title">Reason (optional)</h5>
              <textarea
                className="ntf-reason"
                rows={3}
                maxLength={200}
                placeholder="Reason shown to the client if you reject this request"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            </section>
          )}
        </>
      );
    }

    /* ── Invoice receipt ── */
    if (invoiceDetails && selectedType === "PAYMENT") {
      const clientName =
        `${invoiceDetails.userId?.name || ""} ${invoiceDetails.userId?.surname || ""}`.trim();
      return (
        <>
          <Hero
            Icon={cat.Icon}
            tone={cat.tone}
            title={`Invoice #${String(invoiceDetails._id).slice(-6).toUpperCase()}`}
            subtitle={formatDate(n.createdAt)}
            status={invoiceDetails.status}
          />
          <Stepper
            steps={[
              { label: "Invoice sent", state: "done" },
              {
                label: "Receipt uploaded",
                state: invoiceDetails.receiptPath ? "done" : "todo",
              },
              {
                label: "Your review",
                state: invoiceDetails.status === "paid" ? "done" : "current",
              },
            ]}
          />
          {!invoiceDetails.receiptPath && (
            <div className="ntf-callout">
              <AlertTriangle size={16} />
              No receipt has been uploaded yet.
            </div>
          )}
          <Section title="Client">
            <InfoRow label="Name" value={clientName} />
            <InfoRow label="Email" value={invoiceDetails.userId?.email} copy />
          </Section>
          <Section title="Invoice">
            <InfoRow label="Pack" value={invoiceDetails.packName} />
            <InfoRow
              label="Invoice date"
              value={formatDate(invoiceDetails.invoiceDate)}
            />
          </Section>
          {receiptView && (
            <ReceiptCard
              title="Receipt"
              src={receiptView.url}
              pdf={receiptView.isPdf}
              onOpen={setPreview}
            />
          )}
        </>
      );
    }

    /* ── Payment ── */
    if (paymentDetails) {
      if (!paymentDetails.userId)
        return <Empty>No payment details available.</Empty>;

      // First the notification's pack name, otherwise search in the message
      const sourceText =
        n.packName || n.message || paymentDetails.packName || "";
      const { model, color, cleanName } = extractIphoneInfo(sourceText);

      return (
        <>
          <Hero
            Icon={cat.Icon}
            tone={cat.tone}
            title="Payment"
            subtitle={formatDate(n.createdAt)}
            status={paymentDetails.status}
          />
          <Stats
            items={[
              {
                label: "Total",
                value: paymentDetails.totalPrice,
                tone: "accent",
              },
              { label: "Quantity", value: paymentDetails.quantity },
            ]}
          />
          <Stepper
            steps={decisionSteps("Payment sent", paymentDetails.status)}
          />
          <Section title="Client">
            <InfoRow label="Email" value={paymentDetails.userEmail} copy />
            <InfoRow label="User ID" value={paymentDetails.userId} copy />
          </Section>

          <Section title="Order">
            <InfoRow
              label="Pack name"
              value={cleanName || paymentDetails.packName}
            />
            <InfoRow label="Unit price" value={paymentDetails.price} />

            {model && <InfoRow label="iPhone model" value={model} />}
            {color && <InfoRow label="iPhone color" value={color} />}

            <InfoRow label="Total price" value={paymentDetails.totalPrice} />
          </Section>

          <Section title="Payment">
            <InfoRow label="Type" value={paymentDetails.paymentType} />
            <InfoRow label="Method" value={paymentDetails.paymentMethod} />
            <InfoRow
              label="Facility"
              value={paymentDetails.paymentFacilities}
            />
          </Section>

          {paymentDetails.receiptPhoto && (
            <ReceiptCard
              title="Receipt"
              src={`${API_URL}${paymentDetails.receiptPhoto}`}
              onOpen={setPreview}
            />
          )}
        </>
      );
    }

    /* ── Quiz ── */
    if (selectedType === "QUIZ") {
      const score = Number(
        String(n.message || "").match(/(\d+(\.\d+)?)\s*%/)?.[1],
      );
      const hasScore = !isNaN(score);
      return (
        <>
          <Hero
            Icon={cat.Icon}
            tone={cat.tone}
            title="Quiz passed"
            subtitle={formatDate(n.createdAt)}
          />
          {hasScore && (
            <div className="ntf-score">
              <div className="ntf-score-top">
                <span>Score</span>
                <strong>{score}%</strong>
              </div>
              <ProgressBar value={score} max={100} tone="ok" />
            </div>
          )}
          <Section title="Result">
            <InfoRow label="Message" value={n.message} />
            <InfoRow label="Date" value={formatDate(n.createdAt)} />
          </Section>
          <p className="ntf-question">
            Authorize this user to proceed to payment?
          </p>
        </>
      );
    }

    /* ── Withdrawal ── */
    if (withdrawDetails) {
      const requested = Number(
        String(withdrawDetails.message || "").match(/(\d+(\.\d+)?)/)?.[0],
      );
      const hasRequested = !isNaN(requested);
      const tooHigh = hasRequested && requested > validPoints;
      return (
        <>
          <Hero
            Icon={cat.Icon}
            tone={cat.tone}
            title="Withdrawal request"
            subtitle={formatDate(withdrawDetails.createdAt)}
            status={withdrawDetails.status}
          />
          <Stats
            items={[
              {
                label: "Requested",
                value: hasRequested ? requested : null,
                tone: tooHigh ? "bad" : "accent",
              },
              { label: "Available", value: validPoints, tone: "ok" },
            ]}
          />
          {hasRequested && (
            <ProgressBar
              value={requested}
              max={validPoints}
              tone={tooHigh ? "bad" : "ok"}
            />
          )}
          {tooHigh && withdrawDetails.status === "pending" && (
            <div className="ntf-callout">
              <AlertTriangle size={16} />
              The requested amount is higher than the valid points available.
            </div>
          )}
          <Stepper
            steps={decisionSteps("Request sent", withdrawDetails.status)}
          />
          <Section title="Request">
            <InfoRow label="User ID" value={withdrawDetails.user} copy />
            <InfoRow
              label="Date"
              value={formatDate(withdrawDetails.createdAt)}
            />
          </Section>
        </>
      );
    }

    /* ── Profile ── */
    if (profileDetails) {
      const p = profileDetails;
      const fullName = `${p.name || ""} ${p.surname || ""}`.trim();
      return (
        <>
          <Hero
            avatar={initials(p.name, p.surname)}
            title={fullName || "Profile"}
            subtitle={
              p.isVerified
                ? "Subscription renewal request"
                : "Identity verification request"
            }
            status={
              p.isVerified ? p.subscriptionStatus || "verified" : "pending"
            }
          />
          <Section title="Identity">
            <InfoRow label="Name" value={p.name} />
            <InfoRow label="Surname" value={p.surname} />
            <InfoRow label="Date of birth" value={shortDate(p.dateOfBirth)} />
            <InfoRow label="Gender" value={p.gender} />
          </Section>
          <Section title="Contact">
            <InfoRow label="Email" value={p.email} copy />
            <InfoRow label="Phone number" value={p.phoneNumber} copy />
            <InfoRow label="Mobile" value={p.mobile} copy />
            <InfoRow label="Country" value={p.country} />
            <InfoRow label="Address" value={p.address} />
          </Section>
          <Section title="Subscription">
            <InfoRow label="Plan" value={p.subscriptionPlan} />
            <InfoRow label="Status" value={p.subscriptionStatus} />
            <InfoRow
              label="Valid until"
              value={shortDate(p.subscriptionEndDate)}
            />
          </Section>

          {p.paymentReceipt && (
            <ReceiptCard
              title="Payment receipt"
              src={`${API_URL}/${p.paymentReceipt}`}
              onOpen={setPreview}
            />
          )}

          {!p.isVerified && (
            <section className="ntf-section">
              <h5 className="ntf-section-title">Verification documents</h5>
              <div className="ntf-media-grid">
                <MediaImage
                  title="ID front"
                  src={p.cinFront ? `${API_URL}/${p.cinFront}` : null}
                  onOpen={setPreview}
                />
                <MediaImage
                  title="ID back"
                  src={p.cinBack ? `${API_URL}/${p.cinBack}` : null}
                  onOpen={setPreview}
                />
              </div>
              <div className="ntf-media">
                <h5>Video</h5>
                {p.video ? (
                  <video controls preload="metadata">
                    <source src={`${API_URL}/${p.video}`} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <span>Not available</span>
                )}
              </div>
            </section>
          )}
        </>
      );
    }

    return <Empty>Unable to load the details.</Empty>;
  };

  /* ── Derived values for the list ── */
  const currentCategory = getCategory(selectedType);

  const categoryNotifications = notifications
    .filter((n) => matchesType(n, selectedType))
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const filteredNotifications =
    listFilter === "unread"
      ? categoryNotifications.filter((n) => !n.read)
      : categoryNotifications;

  const groups = [];
  filteredNotifications.forEach((n) => {
    const label = dayLabel(n.createdAt);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(n);
    else groups.push({ label, items: [n] });
  });

  const actions = getActions();

  const headTitle =
    viewMode === "types"
      ? "Notifications"
      : currentCategory
        ? currentCategory.title
        : "Notifications";

  const headCount =
    viewMode === "types"
      ? totalUnread
      : currentCategory
        ? unreadCounts[currentCategory.key] || 0
        : 0;

  /* Tag shown on a list item: for staking requests, shows the decision once taken */
  const itemTag = (notif) => {
    if (notif.type === "STAKE_REQUESTED" && notif.status === "approved")
      return "Approved";
    if (notif.type === "STAKE_REQUESTED" && notif.status === "rejected")
      return "Rejected";
    return TYPE_LABELS[notif.type];
  };

  // === RENDER ===
  return (
    <AdminPopup onClose={closePopup} onEscape={handleEscape}>
      <div className="ntf-shell">
        <div className="adm-topbar">
          <button className="adm-back" onClick={handleBack} aria-label="Back">
            <ArrowLeft /> Back
          </button>
          <h3>{headTitle}</h3>
          {headCount > 0 && viewMode !== "details" && (
            <span className="adm-topbar-count">{headCount} unread</span>
          )}
        </div>

        <div className="adm-body">
          {error && (
            <div className="ntf-error" role="alert">
              <span>{error}</span>
              {viewMode === "types" && (
                <button
                  className="adm-btn danger"
                  onClick={() => {
                    setError(null);
                    setLoading(true);
                    fetchAllData();
                  }}
                >
                  Retry
                </button>
              )}
            </div>
          )}

          {/* === Home: one card per category === */}
          {viewMode === "types" &&
            (loading ? (
              <Skeleton rows={5} />
            ) : (
              <>
                <div className="ntf-home-head">
                  <span className="ntf-bell">
                    <Bell size={18} />
                  </span>
                  <div>
                    <h2>Notifications</h2>
                    <p>
                      {totalUnread > 0
                        ? `${totalUnread} to review`
                        : "You're all caught up"}
                    </p>
                  </div>
                </div>
                <div className="ntf-cards">
                  {CATEGORIES.map(({ key, title, Icon, tone }) => {
                    const { total, unread, subtitle } = getCardSummary(key);
                    return (
                      <button
                        key={key}
                        type="button"
                        className="ntf-card"
                        onClick={() => togglePopup(key, "list")}
                      >
                        <span className={`ntf-icon lg ${tone}`}>
                          <Icon size={18} />
                        </span>
                        <span className="ntf-card-text">
                          <span className="ntf-card-title">{title}</span>
                          <span
                            className={`ntf-card-sub ${unread > 0 ? "alert" : ""}`}
                          >
                            {subtitle}
                          </span>
                          <span className="ntf-card-meta">{total} total</span>
                        </span>
                        {unread > 0 && (
                          <span className="ntf-badge">{unread}</span>
                        )}
                        <ChevronRight className="ntf-card-arrow" />
                      </button>
                    );
                  })}
                </div>
              </>
            ))}

          {/* === Notification list === */}
          {viewMode === "list" && selectedType && currentCategory && (
            <>
              <div className="adm-filters ntf-filters">
                {[
                  ["all", "All", categoryNotifications.length],
                  ["unread", "Unread", unreadCounts[selectedType] || 0],
                ].map(([key, label, count]) => (
                  <button
                    key={key}
                    className={`adm-chip ${listFilter === key ? "active" : ""}`}
                    onClick={() => setListFilter(key)}
                  >
                    {label}
                    <span className="adm-chip-count">{count}</span>
                  </button>
                ))}
              </div>

              {groups.length === 0 ? (
                <Empty>
                  <Bell size={20} />
                  {listFilter === "unread"
                    ? `No unread ${currentCategory.noun} notifications`
                    : `No ${currentCategory.noun} notifications available`}
                </Empty>
              ) : (
                groups.map((group) => (
                  <div key={group.label} className="ntf-group">
                    <div className="ntf-day">{group.label}</div>
                    <ul className="ntf-list">
                      {group.items.map((notif) => {
                        const CatIcon = currentCategory.Icon;
                        const tag = itemTag(notif);
                        return (
                          <li
                            key={notif._id}
                            className={`ntf-item ${!notif.read ? "unread" : ""}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleNotificationClick(notif)}
                            onKeyDown={(e) => {
                              if (e.target !== e.currentTarget) return; // ignore the Delete button
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleNotificationClick(notif);
                              }
                            }}
                          >
                            <span
                              className={`ntf-icon ${currentCategory.tone}`}
                            >
                              <CatIcon size={16} />
                            </span>
                            <div className="ntf-item-main">
                              <p className="ntf-item-msg">{notif.message}</p>
                              <span className="ntf-item-meta">
                                {tag && <span className="ntf-tag">{tag}</span>}
                                <span title={formatDate(notif.createdAt)}>
                                  {shortTime(notif.createdAt)}
                                </span>
                              </span>
                            </div>
                            {!notif.read && (
                              <span className="ntf-dot" aria-label="Unread" />
                            )}
                            <button
                              className="adm-icon-btn sm danger"
                              title="Delete"
                              aria-label="Delete notification"
                              onClick={(e) => {
                                e.stopPropagation();
                                askDeleteNotification(notif);
                              }}
                            >
                              <Trash2 />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))
              )}
            </>
          )}

          {/* === Details === */}
          {viewMode === "details" && selectedNotification && (
            <div className="ntf-details">{renderDetails()}</div>
          )}
        </div>

        {/* Actions always visible at the bottom of the popup */}
        {actions && (
          <div className="ntf-footer">
            {actions.map((a) => (
              <button
                key={a.label}
                className={`adm-btn ${a.kind === "ghost" ? "ntf-ghost" : a.kind}`}
                onClick={a.onClick}
                disabled={a.disabled || busy}
              >
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        onConfirm={runConfirm}
        onCancel={() => setConfirm(null)}
      />

      {preview && (
        <Lightbox preview={preview} onClose={() => setPreview(null)} />
      )}
    </AdminPopup>
  );
};

export default Notifications;